// Mirror of the Firebase Auth password policy configured in the console
// (Authentication → Settings → Password policy). Applied client-side so the
// user gets immediate feedback while creating a password, before any network
// round-trip. The console enforcement mode is "Notify", so this client-side
// check is the primary gate — keep it in sync with the console settings.
export interface PasswordPolicy {
  minLength: number;
  maxLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumeric: boolean;
  requireSpecial: boolean;
}

export const PASSWORD_POLICY: PasswordPolicy = {
  minLength: 8,
  maxLength: 4096,
  requireUppercase: true,
  requireLowercase: false,
  requireNumeric: true,
  requireSpecial: true,
};

export type PasswordCriterion =
  | 'minLength'
  | 'maxLength'
  | 'uppercase'
  | 'lowercase'
  | 'numeric'
  | 'special';

// Anything that isn't an ASCII letter or digit counts as a special character
// (matches Firebase's "non-alphanumeric" requirement).
const SPECIAL = /[^A-Za-z0-9]/;
const UPPER = /[A-Z]/;
const LOWER = /[a-z]/;
const NUMERIC = /[0-9]/;

// Return the list of UNMET criteria; an empty array means the password passes.
export const checkPassword = (
  password: string,
  policy: PasswordPolicy = PASSWORD_POLICY
): PasswordCriterion[] => {
  const unmet: PasswordCriterion[] = [];
  if (password.length < policy.minLength) unmet.push('minLength');
  if (password.length > policy.maxLength) unmet.push('maxLength');
  if (policy.requireUppercase && !UPPER.test(password)) unmet.push('uppercase');
  if (policy.requireLowercase && !LOWER.test(password)) unmet.push('lowercase');
  if (policy.requireNumeric && !NUMERIC.test(password)) unmet.push('numeric');
  if (policy.requireSpecial && !SPECIAL.test(password)) unmet.push('special');
  return unmet;
};

export const isPasswordValid = (
  password: string,
  policy: PasswordPolicy = PASSWORD_POLICY
): boolean => checkPassword(password, policy).length === 0;

// Ordered list of criteria the signup UI should render as a live checklist.
// Excludes maxLength (an extreme edge the user can't realistically hit while
// typing) and any criterion the policy leaves off.
export const activeCriteria = (
  policy: PasswordPolicy = PASSWORD_POLICY
): PasswordCriterion[] => {
  const list: PasswordCriterion[] = ['minLength'];
  if (policy.requireUppercase) list.push('uppercase');
  if (policy.requireLowercase) list.push('lowercase');
  if (policy.requireNumeric) list.push('numeric');
  if (policy.requireSpecial) list.push('special');
  return list;
};

// Add dummy default export for Expo Router (every file under app/ needs one).
export default { PASSWORD_POLICY, checkPassword, isPasswordValid, activeCriteria };
